import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { BRAND } from "@/lib/api";
import { Save, RefreshCw, MessageSquare, Send, PackageCheck, HelpCircle } from "lucide-react";

const SECTIONS = [
  {
    key: "bill_whatsapp",
    icon: Send,
    tint: "bg-blue-50 text-blue-600",
    title: "Bill Send Message",
    description: "Sent when you share a bill on WhatsApp. Includes the public bill link customers can tap to see item-wise prices.",
    vars: ["{customer_name}", "{bill_number}", "{grand_total}", "{balance}", "{order_date}", "{delivery_date}", "{public_link}", "{business_phone}"],
  },
  {
    key: "pickup_confirm",
    icon: PackageCheck,
    tint: "bg-emerald-50 text-emerald-600",
    title: "Pickup Confirmation",
    description: "Sent to customers after they book a pickup on your website.",
    vars: ["{customer_name}", "{pickup_date}", "{pickup_time}", "{address}", "{service_type}", "{business_phone}"],
  },
  {
    key: "pickup_query",
    icon: HelpCircle,
    tint: "bg-orange-50 text-orange-600",
    title: "Pickup / General Query Reply",
    description: "Quick reply to customer queries via WhatsApp.",
    vars: ["{customer_name}", "{business_phone}"],
  },
  {
    key: "ready_for_delivery",
    icon: MessageSquare,
    tint: "bg-violet-50 text-violet-600",
    title: "Order Ready / Out for Delivery",
    description: "Notify customer their order is ready and being delivered.",
    vars: ["{customer_name}", "{bill_number}", "{balance}", "{delivery_date}", "{business_phone}"],
  },
];

const Messages = () => {
  const [templates, setTemplates] = useState({});
  const [defaults, setDefaults] = useState({});
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/settings/messages");
    setTemplates(data.templates);
    setDefaults(data.defaults);
    setDirty({});
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => { setTemplates((t) => ({ ...t, [k]: v })); setDirty((d) => ({ ...d, [k]: true })); };
  const reset = (k) => { setTemplates((t) => ({ ...t, [k]: defaults[k] })); setDirty((d) => ({ ...d, [k]: true })); };

  const insertVar = (key, variable) => {
    const el = document.getElementById(`tpl-${key}`);
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const val = templates[key] || "";
    const next = val.slice(0, start) + variable + val.slice(end);
    set(key, next);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + variable.length, start + variable.length); }, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/messages", { templates });
      toast.success("Templates saved");
      setDirty({});
    } catch (e) { toast.error("Could not save templates"); }
    finally { setSaving(false); }
  };

  const previewFor = (key) => {
    const sample = {
      customer_name: "Rahul Sharma",
      bill_number: "QW1024",
      grand_total: "850",
      balance: "400",
      order_date: "10 Feb 2026",
      delivery_date: "12 Feb 2026",
      pickup_date: "10 Feb 2026",
      pickup_time: "10:30",
      address: "House 24, Sector 4, Rohtak",
      service_type: "Wash & Iron",
      public_link: `${window.location.origin}/bill/QW1024`,
      business_phone: BRAND.phone,
    };
    let t = templates[key] || "";
    Object.keys(sample).forEach((k) => { t = t.replaceAll(`{${k}}`, sample[k]); });
    return t;
  };

  if (loading) return <div className="h-40 bg-slate-100 rounded-xl animate-pulse"/>;

  const anyDirty = Object.keys(dirty).length > 0;

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex flex-wrap justify-between gap-3 items-start">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>Message Templates</h1>
          <p className="text-sm text-slate-500">Edit the WhatsApp messages sent for bills, pickups & queries. Use variables in <code className="text-xs bg-slate-100 px-1 rounded">{'{curly_braces}'}</code>.</p>
        </div>
        <button disabled={!anyDirty || saving} onClick={save} className="qw-btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="messages-save">
          <Save size={16}/>{saving ? "Saving…" : anyDirty ? "Save Changes" : "All Saved"}
        </button>
      </div>

      {SECTIONS.map((s) => (
        <div key={s.key} className="qw-dash-card p-6" data-testid={`msg-card-${s.key}`}>
          <div className="flex items-start gap-4 mb-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.tint}`}><s.icon size={20}/></div>
            <div className="flex-1">
              <div className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Poppins' }}>{s.title}</div>
              <p className="text-sm text-slate-500 mt-0.5">{s.description}</p>
            </div>
            <button onClick={() => reset(s.key)} className="qw-btn-ghost text-xs py-1.5 px-3" data-testid={`msg-reset-${s.key}`}><RefreshCw size={12}/>Reset</button>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div>
              <div className="qw-label flex items-center justify-between">
                <span>Template</span>
                {dirty[s.key] && <span className="text-orange-600 text-xs">unsaved</span>}
              </div>
              <textarea
                id={`tpl-${s.key}`}
                rows={12}
                className="qw-input font-mono text-sm leading-relaxed"
                value={templates[s.key] || ""}
                onChange={(e) => set(s.key, e.target.value)}
                data-testid={`msg-textarea-${s.key}`}
              />
              <div className="mt-3">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Insert variable</div>
                <div className="flex flex-wrap gap-2">
                  {s.vars.map((v) => (
                    <button key={v} type="button" onClick={() => insertVar(s.key, v)} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-mono transition-colors" data-testid={`msg-var-${s.key}-${v.replace(/[{}]/g, "")}`}>{v}</button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <div className="qw-label">Preview (with sample data)</div>
              <div className="rounded-xl bg-emerald-50/50 border border-emerald-100 p-4 text-sm text-slate-800 whitespace-pre-wrap min-h-[200px] font-sans" data-testid={`msg-preview-${s.key}`}>
                {previewFor(s.key)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Messages;
